import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useMeRole } from '../hooks/useMeRole';

/**
 * Market owner dashboard: sales for all vendors in their market (oversight only).
 * Visible only to users with IsOwner in DB. No items/Stripe here.
 */
export default function OwnerDashboard() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { isOwner, loading: roleLoading } = useMeRole();
  const [reportsPeriod, setReportsPeriod] = useState('day');
  const [ownerReports, setOwnerReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect non-owners immediately
  useEffect(() => {
    if (!roleLoading && !isOwner) {
      navigate('/profile', { replace: true });
    }
  }, [isOwner, roleLoading, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/markets/owner/reports?period=${reportsPeriod}`, { getToken });
        if (!res.ok) {
          if (res.status === 403) {
            if (!cancelled) navigate('/', { replace: true });
            return;
          }
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setOwnerReports(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, reportsPeriod, navigate]);

  // Don't render if not an owner
  if (roleLoading) {
    return (
      <SignedIn>
        <div className="flex items-center justify-center py-16">
          <p className="text-2xl text-slate-500 dark:text-slate-400">Loading…</p>
        </div>
      </SignedIn>
    );
  }

  if (!isOwner) {
    return null; // Will redirect via useEffect
  }

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold">Owner dashboard</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          Sales across all vendors in your market.
        </p>
        {error && (
          <div className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setReportsPeriod('day')}
            className={`px-4 py-2 rounded-lg ${reportsPeriod === 'day' ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}`}
          >
            Day
          </button>
          <button
            type="button"
            onClick={() => setReportsPeriod('week')}
            className={`px-4 py-2 rounded-lg ${reportsPeriod === 'week' ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}`}
          >
            Week
          </button>
          <button
            type="button"
            onClick={() => setReportsPeriod('month')}
            className={`px-4 py-2 rounded-lg ${reportsPeriod === 'month' ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}`}
          >
            Month
          </button>
        </div>
        {loading ? (
          <div className="text-slate-500 dark:text-slate-400 py-8">Loading reports…</div>
        ) : ownerReports ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-700 dark:text-blue-300">Total Sales</div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {ownerReports.totalSalesCount}
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-700 dark:text-green-300">Total Net Sales</div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  ${ownerReports.totalNet?.toFixed(2)}
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <div className="text-sm text-purple-700 dark:text-purple-300">Profit (2% of gross)</div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  ${((ownerReports.totalGross ?? 0) * 0.02).toFixed(2)}
                </div>
              </div>
            </div>
            {ownerReports.reports && ownerReports.reports.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-300 dark:border-slate-600">
                      <th className="p-3 text-lg font-semibold">Period</th>
                      <th className="p-3 text-lg font-semibold">Sales Count</th>
                      <th className="p-3 text-lg font-semibold">Net Sales</th>
                      <th className="p-3 text-lg font-semibold">Profit (2%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownerReports.reports.map((report, idx) => (
                      <tr key={idx} className="border-b border-slate-200 dark:border-slate-700">
                        <td className="p-3">{report.period}</td>
                        <td className="p-3">{report.salesCount}</td>
                        <td className="p-3 font-medium">${report.totalNet?.toFixed(2)}</td>
                        <td className="p-3 font-medium text-purple-600 dark:text-purple-400">
                          ${((report.totalGross ?? 0) * 0.02).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">No sales data for this period.</div>
            )}
          </div>
        ) : null}
      </div>
    </SignedIn>
  );
}
